import type { ReactElement, ReactNode } from 'react';
import { FlatList, Pressable, Text, View } from 'react-native';

import { EmptyState } from '../primitives/EmptyState';
import type { PickerStyles } from './pickerStyles';
import type { UiTheme } from '../theme';

// The import tabs' list (#336, out of PickerPlanList when PickerProgramList
// became its second user): the hint above the rows, one separator between
// them, the empty state when there is nothing to import, and the row itself.

/** One tappable row: a name, a sub line, optional glyphs either side. Its
 *  label says what the tap DOES, not only what the row is. */
export function PickerImportRow({
  styles,
  title,
  sub,
  accessibilityLabel,
  onPress,
  leading,
  trailing,
}: {
  styles: PickerStyles;
  title: string;
  sub?: string;
  accessibilityLabel: string;
  onPress: () => void;
  leading?: ReactNode;
  trailing?: ReactNode;
}) {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      onPress={onPress}
      style={({ pressed }) => [styles.row, pressed && styles.rowPressed]}
    >
      {leading}
      <View style={styles.rowBody}>
        <Text style={styles.rowName} numberOfLines={1}>
          {title}
        </Text>
        {sub ? (
          <Text style={styles.rowSub} numberOfLines={1}>
            {sub}
          </Text>
        ) : null}
      </View>
      {trailing}
    </Pressable>
  );
}

export function PickerImportList<T>({
  theme,
  styles,
  data,
  keyExtractor,
  renderItem,
  header,
  hint,
  emptyTitle,
}: {
  theme: UiTheme;
  styles: PickerStyles;
  data: ReadonlyArray<T>;
  keyExtractor: (item: T) => string;
  renderItem: (item: T) => ReactElement;
  /** Above the hint: a back row, say. */
  header?: ReactNode;
  /** The consequence of a tap, said BEFORE the list, and only when there is
   *  something to tap: a caption for a control that is not there misleads. */
  hint?: string;
  emptyTitle: string;
}) {
  return (
    <FlatList
      style={styles.list}
      contentContainerStyle={styles.listContent}
      data={data}
      keyExtractor={keyExtractor}
      keyboardShouldPersistTaps="handled"
      ListHeaderComponent={
        <>
          {header}
          {hint && data.length > 0 ? <Text style={styles.listHint}>{hint}</Text> : null}
        </>
      }
      ListEmptyComponent={
        <View style={styles.center}>
          <EmptyState theme={theme} title={emptyTitle} />
        </View>
      }
      // The separator rides with the row: no component built during render.
      renderItem={({ item, index }) => (
        <>
          {index > 0 ? (
            <View style={[styles.separator, { backgroundColor: theme.palette.border }]} />
          ) : null}
          {renderItem(item)}
        </>
      )}
    />
  );
}
